import { openDB, type IDBPDatabase, type DBSchema } from 'idb';
import { Sentry } from '@/lib/sentry';

const DB_NAME = 'buzzy-word-db';
const DB_VERSION = 2;
const WORD_STORE = 'words';
const PROGRESS_STORE = 'progress';
const PENDING_SYNC_STORE = 'pending_sync';

export interface WordDatabase {
  grade: number;
  words: any[];
  version?: string;
  lastUpdated?: string;
}

export interface SyncItem {
  id?: number;
  type: string;
  data: any;
  timestamp: string;
  synced: boolean;
  retryCount: number;
  lastAttempt?: string | null;
}

// idb requires that store names are string literals in the schema interface.
// Using `as const` store name variables and a mapped interface satisfies the
// DBSchema index-signature constraint without requiring a bare [key: string] index.
interface BuzzyDBSchema extends DBSchema {
  words: {
    key: number;
    value: WordDatabase;
    indexes: { version: string; lastUpdated: string };
  };
  progress: {
    key: number;
    value: any;
  };
  pending_sync: {
    key: number;
    value: SyncItem;
    indexes: { timestamp: string; synced: boolean; type: string };
  };
  [key: string]: any;
}

/**
 * Initialize and open IndexedDB
 */
export async function initDB(): Promise<IDBPDatabase<BuzzyDBSchema>> {
  return openDB<BuzzyDBSchema>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, newVersion) {
      try {
        // Create words object store
        if (!db.objectStoreNames.contains(WORD_STORE)) {
          const wordStore = db.createObjectStore(WORD_STORE, { keyPath: 'grade' });
          wordStore.createIndex('version', 'version', { unique: false });
          wordStore.createIndex('lastUpdated', 'lastUpdated', { unique: false });
        }

        // Create progress object store for future use
        if (!db.objectStoreNames.contains(PROGRESS_STORE)) {
          db.createObjectStore(PROGRESS_STORE, {
            keyPath: 'id',
            autoIncrement: true,
          });
        }

        // Create pending_sync object store for offline sync queue
        if (!db.objectStoreNames.contains(PENDING_SYNC_STORE)) {
          const syncStore = db.createObjectStore(PENDING_SYNC_STORE, {
            keyPath: 'id',
            autoIncrement: true,
          });
          syncStore.createIndex('timestamp', 'timestamp', { unique: false });
          syncStore.createIndex('synced', 'synced', { unique: false });
          syncStore.createIndex('type', 'type', { unique: false });
        }
      } catch (err) {
        Sentry.captureException(err, {
          tags: { module: 'db', operation: 'upgrade' },
          extra: { oldVersion, newVersion, dbName: DB_NAME },
        });
        throw err;
      }
    },
  });
}

/**
 * Save word database for a specific grade
 */
export async function saveWordsToCache(gradeData: WordDatabase): Promise<void> {
  try {
    const db = await initDB();
    await db.put(WORD_STORE, gradeData);
  } catch (err) {
    Sentry.captureException(err, {
      tags: { module: 'db', operation: 'saveWordsToCache' },
      extra: { grade: gradeData.grade },
    });
    throw err;
  }
}

/**
 * Retrieve words for a specific grade from cache
 */
export async function getWordsFromCache(grade: number): Promise<WordDatabase | undefined> {
  try {
    const db = await initDB();
    return await db.get(WORD_STORE, grade);
  } catch (err) {
    Sentry.captureException(err, {
      tags: { module: 'db', operation: 'getWordsFromCache' },
      extra: { grade },
    });
    throw err;
  }
}

/**
 * Check if word data exists for a grade
 */
export async function hasGradeData(grade: number): Promise<boolean> {
  const db = await initDB();
  const data = await db.get(WORD_STORE, grade);
  return !!data;
}

/**
 * Get all cached grades
 */
export async function getAllCachedGrades(): Promise<number[]> {
  const db = await initDB();
  return await db.getAllKeys(WORD_STORE);
}

/**
 * Clear all cached word data
 */
export async function clearWordCache(): Promise<void> {
  try {
    const db = await initDB();
    const tx = db.transaction(WORD_STORE, 'readwrite');
    await tx.objectStore(WORD_STORE).clear();
    await tx.done;
  } catch (err) {
    Sentry.captureException(err, {
      tags: { module: 'db', operation: 'clearWordCache' },
    });
    throw err;
  }
}

/**
 * Check if database needs update based on version
 */
export async function needsUpdate(grade: number, remoteVersion: string): Promise<boolean> {
  const localData = await getWordsFromCache(grade);
  if (!localData) return true;
  return localData.version !== remoteVersion;
}

/**
 * Add item to sync queue
 */
export async function addToSyncQueue(type: string, data: any): Promise<number> {
  try {
    const db = await initDB();
    const item: Omit<SyncItem, 'id'> = {
      type,
      data,
      timestamp: new Date().toISOString(),
      retryCount: 0,
      synced: false,
      lastAttempt: null,
    };
    return await db.add(PENDING_SYNC_STORE, item as SyncItem);
  } catch (err) {
    Sentry.captureException(err, {
      tags: { module: 'db', operation: 'addToSyncQueue' },
      extra: { type },
    });
    throw err;
  }
}

/**
 * Get all pending sync items
 */
export async function getPendingSyncItems(): Promise<SyncItem[]> {
  try {
    const db = await initDB();
    const allItems = await db.getAll(PENDING_SYNC_STORE);
    return allItems.filter(item => item.synced !== true);
  } catch (err) {
    Sentry.captureException(err, {
      tags: { module: 'db', operation: 'getPendingSyncItems' },
    });
    throw err;
  }
}

/**
 * Get all sync items
 */
export async function getAllSyncItems(): Promise<SyncItem[]> {
  const db = await initDB();
  return await db.getAll(PENDING_SYNC_STORE);
}

/**
 * Mark sync item as synced
 */
export async function markSyncItemSynced(id: number): Promise<void> {
  try {
    const db = await initDB();
    const item = await db.get(PENDING_SYNC_STORE, id);
    if (item) {
      item.synced = true;
      await db.put(PENDING_SYNC_STORE, item);
    }
  } catch (err) {
    Sentry.captureException(err, {
      tags: { module: 'db', operation: 'markSyncItemSynced' },
      extra: { id },
    });
    throw err;
  }
}

/**
 * Increment retry count for sync item
 */
export async function incrementSyncRetry(id: number): Promise<void> {
  try {
    const db = await initDB();
    const item = await db.get(PENDING_SYNC_STORE, id);
    if (item) {
      item.retryCount = (item.retryCount || 0) + 1;
      item.lastAttempt = new Date().toISOString();
      await db.put(PENDING_SYNC_STORE, item);
    }
  } catch (err) {
    Sentry.captureException(err, {
      tags: { module: 'db', operation: 'incrementSyncRetry' },
      extra: { id },
    });
    throw err;
  }
}

/**
 * Clear synced items from queue
 */
export async function clearSyncedItems(): Promise<number> {
  try {
    const db = await initDB();
    const tx = db.transaction(PENDING_SYNC_STORE, 'readwrite');
    const store = tx.objectStore(PENDING_SYNC_STORE);

    const allItems = await store.getAll();
    const syncedItems = allItems.filter(item => item.synced === true);

    for (const item of syncedItems) {
      if (item.id !== undefined) {
        await store.delete(item.id);
      }
    }

    await tx.done;
    return syncedItems.length;
  } catch (err) {
    Sentry.captureException(err, {
      tags: { module: 'db', operation: 'clearSyncedItems' },
    });
    throw err;
  }
}

/**
 * Delete a specific sync item
 */
export async function deleteSyncItem(id: number): Promise<void> {
  const db = await initDB();
  await db.delete(PENDING_SYNC_STORE, id);
}

/**
 * Clear all sync items
 */
export async function clearAllSyncItems(): Promise<void> {
  const db = await initDB();
  const tx = db.transaction(PENDING_SYNC_STORE, 'readwrite');
  await tx.objectStore(PENDING_SYNC_STORE).clear();
  await tx.done;
}
